import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import { comics } from '@/mocks/comics';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const comic = useMemo(() => comics.find((c) => c.id === id), [id]);
  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  const relatedComics = useMemo(() => {
    const ids = comic ? [] : article?.relatedComicIds || [];
    return ids.map((cid) => comics.find((c) => c.id === cid)).filter(Boolean);
  }, [comic, article]);

  const relatedArticles = useMemo(() => {
    const ids = article?.relatedArticleIds || [];
    return ids.map((aid) => articles.find((a) => a.id === aid)).filter(Boolean);
  }, [article]);

  const catName = useMemo(() => {
    if (!comic && !article) return '';
    const cat = categories.find((c) => c.name === ((comic || article)?.category || ''));
    return cat?.slug || '';
  }, [comic, article]);

  if (!comic && !article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-3">内容未找到</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const item = comic || article;
  if (!item) return null;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
  ];

  if (catName) {
    const cat = categories.find((c) => c.name === item.category);
    breadcrumbItems.push({ label: item.category, href: `/category/${cat?.slug || catName}` });
  }

  breadcrumbItems.push({ label: item.title });

  return (
    <>
      <SEOSchema
        type="Article"
        data={{
          headline: item.title,
          description: item.summary,
          author: { '@type': 'Person', name: item.author },
          datePublished: item.publishDate,
          url: `https://www.hanmancomic.com/article/${item.id}`,
          publisher: {
            '@type': 'Organization',
            name: '韩漫漫画网站',
          },
        }}
      />
      <SEOSchema
        type="WebPage"
        data={{
          name: `${item.title} - 韩漫漫画网站`,
          url: `https://www.hanmancomic.com/article/${item.id}`,
        }}
      />

      <Navbar />

      <main className="min-h-screen pt-20 pb-14">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <article className="mt-8">
            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label">
                  {item.category}
                </span>
                {item.tags?.map((tag: string) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 leading-tight mb-4">
                {item.title}
              </h1>

              <div className="flex items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {item.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {item.publishDate}
                </span>
                {'chapters' in item && (
                  <span className="flex items-center gap-1">
                    <i className="ri-book-open-line"></i> {item.chapters} 话
                  </span>
                )}
                {'status' in item && (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-label ${
                    item.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {item.status}
                  </span>
                )}
              </div>
            </header>

            {'coverImage' in item && (
              <div className="relative aspect-[16/9] rounded-lg overflow-hidden bg-background-100 mb-8">
                <img
                  src={item.coverImage}
                  alt={item.title}
                  title={`${item.title} - 韩漫漫画网站`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            )}

            {'rating' in item && (
              <div className="flex flex-wrap items-center gap-6 p-5 bg-background-100 rounded-lg mb-8">
                <div className="text-center">
                  <div className="text-3xl font-heading text-primary-600">{item.rating}</div>
                  <div className="text-xs text-foreground-500 mt-1">评分</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-heading text-foreground-900">
                    {item.views > 10000 ? `${(item.views / 10000).toFixed(0)}万` : item.views}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">阅读量</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-heading text-foreground-900">{item.chapters}</div>
                  <div className="text-xs text-foreground-500 mt-1">总章节</div>
                </div>
                <div className="ml-auto">
                  <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 rounded-full font-label text-sm transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-play-circle-line mr-2"></i>开始阅读
                  </button>
                </div>
              </div>
            )}

            <div className="prose-custom">
              {'summary' in item && (
                <div className="bg-accent-50 border-l-4 border-accent-400 p-4 md:p-5 rounded-r-lg mb-8">
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    <strong className="text-foreground-900">简介：</strong>{item.summary || ('description' in item ? (item as { description: string }).description : '')}
                  </p>
                </div>
              )}

              {article && 'content' in item && (
                <div
                  className="article-content text-sm md:text-base text-foreground-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: (item as { content: string }).content }}
                />
              )}

              {comic && !article && (
                <div className="text-sm md:text-base text-foreground-800 leading-relaxed space-y-4">
                  <p>{item.description || item.summary}</p>
                  <p>这部作品自连载以来，凭借精彩的剧情和精美的画风，吸引了大量读者关注。无论你是第一次接触韩漫，还是资深漫迷，都能在其中找到属于自己的乐趣。</p>
                  <p>点击上方"开始阅读"按钮，即可享受免费下拉式高清阅读体验，无广告干扰，让阅读更加纯粹。</p>
                </div>
              )}
            </div>
          </article>

          {relatedComics.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/60">
              <h2 className="font-heading text-xl text-foreground-900 mb-5">相关漫画推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-product-shop>
                {relatedComics.map((rc) => rc && (
                  <Link key={rc.id} to={`/article/${rc.id}`} className="group cursor-pointer">
                    <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-100 mb-2">
                      <img src={rc.coverImage} alt={rc.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <h3 className="text-xs font-label font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">{rc.title}</h3>
                    <span className="text-[11px] text-foreground-500">{rc.author}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/60">
              <h2 className="font-heading text-xl text-foreground-900 mb-5">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedArticles.map((ra) => ra && (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group flex gap-4 p-4 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <div className="w-24 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                      <img src={ra.coverImage} alt={ra.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-label font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">{ra.title}</h3>
                      <p className="text-xs text-foreground-500 mt-1">{ra.publishDate} · {ra.author}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <style>{`
        .article-content h2 {
          font-family: var(--font-heading);
          font-size: 1.25rem;
          color: oklch(var(--foreground-900));
          margin-top: 1.75rem;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid oklch(var(--background-200));
        }
        .article-content h3 {
          font-family: var(--font-heading);
          font-size: 1.1rem;
          color: oklch(var(--foreground-900));
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .article-content p {
          margin-bottom: 1rem;
          line-height: 1.75;
        }
        .article-content strong {
          color: oklch(var(--primary-600));
        }
      `}</style>

      <Footer />
    </>
  );
}